import { Injectable } from '@angular/core';

/**
 * Centralized wrapper around the browser's Web Notifications API. Owns the
 * permission state and the shared "only alert when the tab isn't focused"
 * policy so every feature (chat pokes/mutes, moderation prompts, …) raises
 * desktop notifications the same way.
 */
@Injectable({
  providedIn: 'root',
})
export class DesktopNotificationService {
  #permission?: NotificationPermission;

  /** Default icon shown on notifications when a caller doesn't provide one. */
  get #defaultIcon(): string {
    return new URL('apple-touch-icon.png', document.baseURI).href;
  }

  /**
   * Ask the browser for permission to show desktop notifications. Safe to call
   * repeatedly: once the user has decided, the browser resolves immediately
   * without re-prompting.
   */
  requestPermission(): void {
    if (!('Notification' in window)) return;
    Notification.requestPermission()
      .then((permission) => {
        this.#permission = permission;
      })
      .catch(() => {});
  }

  /**
   * Show a desktop notification. Suppressed while the tab/window is focused —
   * the in-app UI already surfaces the event — unless `force` is set for
   * high-signal events (e.g. the local user being muted). No-ops when the API
   * is unavailable or permission has not been granted.
   */
  notify(title: string, options: { body?: string; icon?: string; force?: boolean } = {}): void {
    if (!('Notification' in window)) return;
    if (!options.force && document.hasFocus()) return;
    const permission = this.#permission ?? Notification.permission;
    if (permission !== 'granted') return;
    new Notification(title, {
      body: options.body,
      icon: options.icon ?? this.#defaultIcon,
    });
  }
}
